import { useState, useEffect, useMemo } from "react";
import { Link2, ArrowRight, CheckCircle, XCircle, RefreshCw, Files, ArrowUpRight, AlertTriangle } from "lucide-react";
import { useOrgId } from "../hooks/useOrgId";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";

// A real truncation bug (see lat.md/astro.md#Write-Back Model) collapses
// content_length_after to a fraction of content_length_before — flag anything
// that shrank by more than the injected <a> tags could plausibly account for.
function LengthCheck({ before, after, tailBefore, tailAfter }) {
  const [showTail, setShowTail] = useState(false);
  if (before == null || after == null) return null;
  const suspicious = after < before * 0.9;
  return (
    <div data-testid="length-check" className={`px-3 py-2 text-xs border rounded-lg ${suspicious ? "bg-red-50 border-red-100 text-red-600" : "bg-green-50 border-green-100 text-green-700"}`}>
      <div className="flex items-center gap-2">
        {suspicious ? <AlertTriangle size={13} strokeWidth={2} className="flex-shrink-0" /> : <CheckCircle size={13} strokeWidth={2} className="flex-shrink-0" />}
        <span className="font-bold">
          {suspicious ? "Possible truncation — " : "Length check OK — "}
          {before.toLocaleString()} → {after.toLocaleString()} chars
        </span>
        {tailBefore != null && (
          <button onClick={() => setShowTail(v => !v)} className="ml-auto underline font-bold">
            {showTail ? "Hide" : "Show"} end of article
          </button>
        )}
      </div>
      {showTail && (
        <div className="mt-2 space-y-2 font-mono">
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase text-zinc-400 mb-1">Before (last 200 chars)</p>
            <p className="bg-white border border-zinc-100 rounded p-2 text-zinc-500 whitespace-pre-wrap break-words">...{tailBefore}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase text-zinc-400 mb-1">After (last 200 chars)</p>
            <p className="bg-white border border-zinc-100 rounded p-2 text-zinc-500 whitespace-pre-wrap break-words">...{tailAfter}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function EditPreview({ edit }) {
  return (
    <div data-testid={`edit-preview-${edit.target_wp_post_id}`} className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 text-xs">
      <div className="flex items-center gap-2 mb-2 text-zinc-500">
        <span className="text-[10px] font-bold tracking-widest uppercase">Anchor:</span>
        <code className="bg-zinc-900 text-white px-1.5 py-0.5 font-bold rounded">{edit.anchor_text}</code>
        <ArrowRight size={12} strokeWidth={2} />
        <span className="text-zinc-900 truncate flex-1">{edit.target_title}</span>
      </div>
      <p className="text-zinc-400 leading-relaxed font-mono whitespace-pre-wrap break-words">
        ...{edit.snippet_before}
        <span className="bg-yellow-100 text-zinc-900 px-0.5 font-bold">{edit.anchor_text}</span>
        {edit.snippet_after}...
      </p>
    </div>
  );
}

function ProposalCard({ proposal, onApply, applying, result }) {
  const [open, setOpen] = useState(false);
  const applied = !!proposal.applied_at || result?.success;
  return (
    <div data-testid={`proposal-card-${proposal.wp_post_id}`} className="bg-white rounded-xl border border-zinc-100 shadow-sm">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-zinc-100">
        <button
          onClick={() => setOpen(v => !v)}
          data-testid={`proposal-toggle-${proposal.wp_post_id}`}
          className="flex-1 text-left"
        >
          <p className="text-sm font-bold text-zinc-900 line-clamp-1">
            {proposal.title}
          </p>
          <p className="text-[10px] text-zinc-400 mt-0.5">
            {proposal.primary_topic && (
              <span className="bg-zinc-900 text-white px-1 py-0.5 mr-2 font-bold tracking-widest uppercase rounded-full">{proposal.primary_topic}</span>
            )}
            <span>{proposal.edit_count} link{proposal.edit_count === 1 ? "" : "s"} ready</span>
          </p>
        </button>
        {proposal.wp_url && (
          <a href={proposal.wp_url} target="_blank" rel="noopener noreferrer"
            data-testid={`proposal-wp-link-${proposal.wp_post_id}`}
            onClick={e => e.stopPropagation()}
            className="text-zinc-400 hover:text-zinc-900" title="Open live page"
          >
            <ArrowUpRight size={14} />
          </a>
        )}
        {applied ? (
          <span data-testid={`proposal-applied-${proposal.wp_post_id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold tracking-widest uppercase bg-green-100 text-green-700 rounded-full">
            <CheckCircle size={11} strokeWidth={2} /> APPLIED
          </span>
        ) : (
          <button
            onClick={() => onApply(proposal)}
            disabled={applying}
            data-testid={`proposal-apply-${proposal.wp_post_id}`}
            className="px-4 py-1.5 text-xs font-bold bg-zinc-900 hover:bg-zinc-800 text-white disabled:opacity-40 transition-colors rounded-lg"
          >
            {applying ? "Applying..." : "Apply"}
          </button>
        )}
      </div>
      {open && (
        <div className="p-5 space-y-2">
          {proposal.edits.map((e, i) => <EditPreview key={i} edit={e} />)}
          {result && result.success && (
            <LengthCheck
              before={result.content_length_before}
              after={result.content_length_after}
              tailBefore={result.content_tail_before}
              tailAfter={result.content_tail_after}
            />
          )}
          {result && !result.success && (
            <div data-testid={`proposal-error-${proposal.wp_post_id}`}
              className="flex items-start gap-2 px-3 py-2 text-xs border bg-red-50 border-red-100 text-red-600 rounded-lg">
              <XCircle size={14} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
              <span>{result.message}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Linking() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxLinks, setMaxLinks] = useState(3);
  const [applyingId, setApplyingId] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [results, setResults] = useState({});
  const [bulkResult, setBulkResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getLinkProposals(maxLinks);
      setProposals(data.proposals || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const orgId = useOrgId();
  useEffect(() => { load(); }, [maxLinks, orgId]);

  const pending = useMemo(
    () => proposals.filter(p => !p.applied_at && !results[p.wp_post_id]?.success),
    [proposals, results]
  );

  const applyOne = async proposal => {
    setApplyingId(proposal.wp_post_id);
    try {
      const r = await api.applyLinks({
        wp_post_id: proposal.wp_post_id,
        edits: proposal.edits.map(e => ({
          target_wp_post_id: e.target_wp_post_id,
          target_url: e.target_url,
          anchor_text: e.anchor_text,
          match_start: e.match_start,
          match_end: e.match_end,
        })),
      });
      setResults(prev => ({ ...prev, [proposal.wp_post_id]: r }));
      if (r.success) await load();
    } catch (e) {
      setResults(prev => ({ ...prev, [proposal.wp_post_id]: { success: false, message: e.response?.data?.detail || e.message } }));
    } finally { setApplyingId(null); }
  };

  // Apply-all runs as a background job on the backend (one CMS write per
  // article, sequential — too slow to hold a single HTTP request open
  // without hitting a browser/proxy timeout). Poll status until done.
  const pollApplyAllStatus = async () => {
    for (;;) {
      const state = await api.getApplyAllStatus();
      setBulkResult({ success_count: state.success_count || 0, total: state.total || 0 });
      if (state.results?.length) {
        setResults(prev => {
          const map = { ...prev };
          for (const item of state.results) map[item.wp_post_id] = item;
          return map;
        });
      }
      if (state.status === "done" || state.status === "error" || state.status === "idle") {
        if (state.status === "error") setBulkResult({ success_count: 0, total: pending.length, error: state.message });
        return;
      }
      await new Promise(res => setTimeout(res, 2000));
    }
  };

  const applyAll = async () => {
    if (pending.length === 0) return;
    if (!window.confirm(`Apply internal links to ${pending.length} article${pending.length === 1 ? "" : "s"}? Each article is saved straight to your CMS.`)) return;
    setBulkApplying(true);
    setBulkResult(null);
    try {
      await api.applyAllLinks(pending.map(p => ({ wp_post_id: p.wp_post_id })), maxLinks);
      await pollApplyAllStatus();
      await load();
    } catch (e) {
      setBulkResult({ success_count: 0, total: pending.length, error: e.response?.data?.detail || e.message });
    } finally { setBulkApplying(false); }
  };

  return (
    <div data-testid="linking-page" className="p-6 sm:p-8 max-w-5xl mx-auto">
      <div className="mb-8 pb-6 border-b border-zinc-100 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Auto-Inject</p>
          <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">
            Internal Linking
          </h1>
          <p className="text-xs text-zinc-500 mt-2 max-w-xl">
            Preview anchor-text matches found in each article and push them as internal links to your CMS. With Astro each save is a GitHub commit you can roll back; with Webflow, edits are published immediately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] tracking-widest uppercase font-bold text-zinc-500">Max links / article</label>
          <select
            value={maxLinks} onChange={e => setMaxLinks(Number(e.target.value))}
            data-testid="max-links-select"
            className="px-2 py-1.5 text-xs border border-zinc-100 bg-white focus:outline-none focus:ring-2 rounded-lg"
            style={{ "--tw-ring-color": PRIMARY }}
          >
            {[1, 2, 3, 5, 8].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            onClick={load} disabled={loading}
            data-testid="refresh-proposals"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-zinc-100 hover:bg-zinc-50 disabled:opacity-40 rounded-lg transition-colors"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={applyAll} disabled={bulkApplying || pending.length === 0}
            data-testid="apply-all-button"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40 rounded-lg transition-colors"
          >
            <Link2 size={12} strokeWidth={2} />
            {bulkApplying ? "Applying..." : `Apply All (${pending.length})`}
          </button>
        </div>
      </div>

      {bulkResult && (
        <div data-testid="bulk-result"
          className={`mb-4 px-4 py-3 text-sm border rounded-lg ${bulkResult.success_count === bulkResult.total ? "bg-green-50 border-green-200 text-green-700" : "bg-orange-50 border-orange-200 text-orange-700"}`}
        >
          Applied to <strong>{bulkResult.success_count}/{bulkResult.total}</strong> articles.
          {bulkResult.error && ` Error: ${bulkResult.error}`}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-400 py-8">Computing proposals...</div>
      ) : proposals.length === 0 ? (
        <div className="bg-white rounded-xl border border-zinc-100 p-12 text-center shadow-sm">
          <Files size={40} color="#CCC" className="mx-auto" />
          <p className="text-sm text-zinc-500 mt-4">
            No linking proposals available. Run a sync so we have related articles + topic keywords.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {proposals.map(p => (
            <ProposalCard
              key={p.wp_post_id}
              proposal={p}
              onApply={applyOne}
              applying={applyingId === p.wp_post_id}
              result={results[p.wp_post_id]}
            />
          ))}
        </div>
      )}
    </div>
  );
}
